/**
 * 家园 fixture：宠物外出旅行、主人守家；一小块菜园与统一钱包（travel_coin 唯一口径）。
 * 作物、数值均为演示值，不是已冻结的经济配置。农场动作按幂等键只结算一次（模拟服务端语义）。
 */
import type { LifecycleOrder, LifecycleResult, MarketReceipt, MarketOperation } from "@/features/collection/marketLifecycle";
import type { FarmActionRequestInput, FarmActionResult, GuardState, HomeSnapshot, InventoryItem, MarketView, PatrolResult, PlotSummary } from "@/shared/contracts";
import { ApiError } from "@/shared/api/errors";
import { fixtureHomeWelcome } from "./reception";
import { atMin, fixturePet } from "./world";
import { FIXTURE_CROPS } from "./farm";
import { consumeFixtureSeed, fixtureCollection } from "./social";

interface HomeState {
  version: number;
  balance: number;
  plots: PlotSummary[];
  pantry: Record<string, number>;
  patrolUntil: number | null;
  patrolStarted: number | null;
  fulfilled: Set<string>;
}

const LABELS: Record<string, { label: string; price: number }> = {
  sun_pea: { label: "太阳豌豆", price: 2 },
  star_tomato: { label: "星星番茄", price: 2 },
  moon_radish: { label: "月光萝卜", price: 3 },
  sea_salt_pea: { label: "海盐豌豆", price: 4 },
};

const state: HomeState = {
  version: 1,
  balance: 120,
  plots: [
    { plot_id: "fx-plot-1", cycle_id: "fx-cycle-1", crop_key: "star_tomato", crop_label: "星星番茄", stage: "ripe", ripe_at: atMin(-30), steal_total: 3, steal_remaining: 2 },
    { plot_id: "fx-plot-2", cycle_id: "fx-cycle-2", crop_key: "moon_radish", crop_label: "月光萝卜", stage: "growing", ripe_at: atMin(95), steal_total: 3, steal_remaining: 3 },
    { plot_id: "fx-plot-3", cycle_id: null, crop_key: null, crop_label: null, stage: "empty", ripe_at: null, steal_total: null, steal_remaining: null },
  ],
  pantry: { star_tomato: 3 },
  patrolUntil: null,
  patrolStarted: null,
  fulfilled: new Set(),
};

function pantryList(): InventoryItem[] {
  return Object.entries(state.pantry)
    .filter(([, qty]) => qty > 0)
    .map(([key, qty]) => ({ item_key: key, label: LABELS[key]?.label ?? key, qty, unit_price: LABELS[key]?.price ?? 1, tradable: true }));
}

function guardState(): GuardState {
  const now = Date.now();
  const until = state.patrolUntil && state.patrolUntil > now ? new Date(state.patrolUntil).toISOString() : null;
  const next = state.patrolStarted && state.patrolStarted + 30 * 60_000 > now ? new Date(state.patrolStarted + 30 * 60_000).toISOString() : null;
  // 演示宠物在旅途中：只有主人巡院窗口内才算有人守着。
  return until ? { guarding: true, basis: "owner_patrol", until, next_patrol_at: next } : { guarding: false, basis: "none", until: null, next_patrol_at: next };
}

function wallet() {
  return { currency: "travel_coin", balance: state.balance, updated_at: new Date().toISOString() };
}

export function fixturePatrol(): PatrolResult {
  const now = Date.now();
  if (!(state.patrolUntil && state.patrolUntil > now)) {
    if (state.patrolStarted && state.patrolStarted + 30 * 60_000 > now) {
      throw new ApiError({ kind: "http", status: 409, code: "CONFLICT", message: "刚巡过院，歇一会儿再去。", details: { reason: "cooldown" } });
    }
    state.patrolStarted = now;
    state.patrolUntil = now + 10 * 60_000;
  }
  return { guard: guardState(), message: "你在院子里转了一圈，这段时间邻居摘不走菜。（演示）" };
}

// Facts are seeded once at module initialization; GET never issues/replaces orders.
const issueTime = new Date().toISOString();
const hkDay = new Date(Date.parse(issueTime) + 8 * 3600000).toISOString().slice(0, 10);
const expires = new Date(Date.parse(hkDay + 'T00:00:00Z') + 16 * 3600000).toISOString();
const issuedOrders: LifecycleOrder[] = [
 {order_id:'fx-order-1',resident:'鹦鹉邮差',item_key:'star_tomato',item_label:LABELS.star_tomato.label,qty:3,reward:9,shop_value:6,fulfilled:false,can_fulfill:true,expires_at:expires,status:'available',issued_at:issueTime,origin:'home_activation',rule_version:'hk-daily-two-v1',issued_unit_price:2,generation_ref:`fixture:fx-home-001:${hkDay}:0`,cancelled_at:null,settled_pet_id:null,settled_at:null,receipt:null,settlement_status:'not_settled'},
 {order_id:'fx-order-2',resident:'松鼠面包师',item_key:'sun_pea',item_label:LABELS.sun_pea.label,qty:2,reward:6,shop_value:4,fulfilled:false,can_fulfill:false,expires_at:expires,status:'available',issued_at:issueTime,origin:'home_activation',rule_version:'hk-daily-two-v1',issued_unit_price:2,generation_ref:`fixture:fx-home-001:${hkDay}:1`,cancelled_at:null,settled_pet_id:null,settled_at:null,receipt:null,settlement_status:'not_settled'},
];
const marketSettled = new Map<string, LifecycleResult>();
const marketFingerprints = new Map<string, string>();
const cancellations = new Map<string, {orderId:string;result:LifecycleOrder}>();
function marketError(reason:string,message:string,status=409):never {throw new ApiError({kind:'http',status,code:status===404?'NOT_FOUND':'CONFLICT',message,details:{reason}});}
function fixtureOrders(): LifecycleOrder[] {
 return issuedOrders.map(o=>({...o,fulfilled:state.fulfilled.has(o.order_id),status:state.fulfilled.has(o.order_id)?'fulfilled':o.status==='cancelled'?'cancelled':Date.parse(o.expires_at)<=Date.now()?'expired':'available',can_fulfill:!state.fulfilled.has(o.order_id)&&o.status!=='cancelled'&&Date.parse(o.expires_at)>Date.now()&&(state.pantry[o.item_key]??0)>=o.qty,shop_value:o.qty*LABELS[o.item_key].price}));
}
export function fixtureMarket(): MarketView {
 return {pantry:pantryList(),wallet:wallet(),orders:fixtureOrders(),player_listing_enabled:false,player_listing_note:'和别的家之间互相买卖还没开放；这里是杂货铺收购和居民订单（演示）。',data_origin:'fixture'};
}
export function fixtureOrderDetail(orderId:string):LifecycleOrder {const o=fixtureOrders().find(o=>o.order_id===orderId);if(!o)return marketError('missing','没有这张订单。',404);return structuredClone(o);}
function fixtureReceipt(kind:'sell'|'order',key:string,item:string,qty:number,price:number|null,coins:number,petId:string,orderId:string|null):MarketReceipt {
 return {receipt_id:`fixture-receipt:${kind}:${key}`,kind,home_id:'fx-home-001',settled_pet_id:petId,order_id:orderId,operation_key:key,item_key:item,qty,unit_price:price,coins,economy_key:`fixture-economy:${kind}:${key}`,tx_id:`fixture-tx:${kind}:${key}`,created_at:new Date().toISOString()};
}
export function fixtureSell(itemKey:string,qty:number,key:string,petId?:string|null,expectedUnitPrice?:number):LifecycleResult {
 const fingerprint=JSON.stringify({itemKey,qty,petId:petId??fixturePet.pet_id,expectedUnitPrice});const replay=marketSettled.get(key);
 if(replay){if(marketFingerprints.get(key)!==fingerprint)return marketError('idempotency_conflict','同一次操作的内容不同。');return structuredClone(replay);}
 const price=LABELS[itemKey]?.price??1;
 if(expectedUnitPrice!==undefined&&expectedUnitPrice!==price)return marketError('price_changed','当前收购价已变化，请查看新报价。');
 if(!Number.isSafeInteger(qty)||qty<=0||qty>99)return marketError('invalid_qty','请选择有效数量。');
 if((state.pantry[itemKey]??0)<qty)return marketError('not_enough','仓库里不够。');
 state.pantry[itemKey]-=qty;const coins=qty*price;state.balance+=coins;
 const receipt=fixtureReceipt('sell',key,itemKey,qty,price,coins,petId??fixturePet.pet_id,null);
 const result:LifecycleResult={wallet:wallet(),pantry:pantryList(),gained_coins:coins,message:`杂货铺收下了 ${qty} 个${LABELS[itemKey]?.label??itemKey}，+${coins} 星币。（演示）`,receipt,settled_pet_id:receipt.settled_pet_id,settlement_status:'verified'};
 marketFingerprints.set(key,fingerprint);marketSettled.set(key,structuredClone(result));return result;
}
export function fixtureFulfill(orderId:string,_key?:string,petId?:string|null):LifecycleResult {
 const order=fixtureOrderDetail(orderId),record=issuedOrders.find(o=>o.order_id===orderId)!;
 if(order.status==='cancelled'||order.status==='expired')return marketError(order.status,'这张订单已不能交货。');
 if(!order.fulfilled){
  if(!order.can_fulfill)return marketError('not_enough','仓库里不够。');
  state.pantry[order.item_key]-=order.qty;state.balance+=order.reward;state.fulfilled.add(orderId);
  const receipt=fixtureReceipt('order',orderId,order.item_key,order.qty,order.issued_unit_price??null,order.reward,petId??fixturePet.pet_id,orderId);
  Object.assign(record,{status:'fulfilled',fulfilled:true,can_fulfill:false,settled_pet_id:receipt.settled_pet_id,settled_at:receipt.created_at,receipt,settlement_status:'verified'});
 }
 return {wallet:wallet(),pantry:pantryList(),gained_coins:record.receipt?.coins??0,message:'原交货收款记录已保存，不会重复支付。（演示）',receipt:record.receipt??null,settled_pet_id:record.settled_pet_id??null,settlement_status:record.receipt?'verified':'legacy_unverified'};
}
export function fixtureCancelOrder(orderId:string,key:string):LifecycleOrder {
 const old=cancellations.get(key);if(old){if(old.orderId!==orderId)return marketError('idempotency_conflict','同一次操作的订单不同。');return structuredClone(old.result);}
 const order=fixtureOrderDetail(orderId),record=issuedOrders.find(o=>o.order_id===orderId)!;
 if(order.fulfilled||order.status==='expired')return marketError('not_available','这张订单已不能取消。');
 if(record.status!=='cancelled')Object.assign(record,{status:'cancelled',can_fulfill:false,cancelled_at:new Date().toISOString()});
 const result=fixtureOrderDetail(orderId);cancellations.set(key,{orderId,result:structuredClone(result)});return result;
}
export function fixtureSaleOperation(key:string,petId?:string|null):MarketOperation {
 const receipt=marketSettled.get(key)?.receipt;return receipt?.kind==='sell'&&receipt.settled_pet_id===(petId??fixturePet.pet_id)?{operation_key:key,status:'committed',receipt:structuredClone(receipt)}:{operation_key:key,status:'unknown',receipt:null};
}

const settled = new Map<string,{fingerprint:string;result:FarmActionResult}>();
let farmCycleSequence=0;
/** Pure bounded projection: initial quota3 with1 already picked becomes catalog2 with1 left. */
function projectedFarmPlot(plot:PlotSummary,now:number):PlotSummary {
  const crop=FIXTURE_CROPS.find(c=>c.crop_key===plot.crop_key);
  if(!crop||plot.stage==="empty"||plot.stage==="harvested")return {...plot};
  const taken=typeof plot.steal_total==="number"&&typeof plot.steal_remaining==="number"?Math.max(0,plot.steal_total-plot.steal_remaining):0;
  return {...plot,crop_label:crop.label,stage:plot.stage==="growing"&&plot.ripe_at&&Number.isFinite(Date.parse(plot.ripe_at))&&Date.parse(plot.ripe_at)<=now?"ripe":plot.stage,steal_total:crop.steal_total,steal_remaining:Math.max(0,crop.steal_total-Math.min(crop.steal_total,taken))};
}
function farmError(reason:string,message:string,status=409):never {
  throw new ApiError({kind:"http",status,code:status===404?"NOT_FOUND":status===422?"VALIDATION_FAILED":"CONFLICT",message,details:{reason}});
}

export function fixtureHomeSnapshot(): HomeSnapshot {
  return {
    home_id: "fx-home-001",
    server_time: new Date().toISOString(),
    version: state.version,
    pet: fixturePet,
    presence: "in_transit",
    guard: guardState(),
    wallet: wallet(),
    pantry: pantryList(),
    plots: state.plots.map(plot=>projectedFarmPlot(plot,Date.now())),
    journey: { journey_id: "fx-journey-001", itinerary_version: 1, headline: "TA 正在旅途中（演示行程）", current_visit_id: null, current_leg_id: "fx-leg-flight" },
    welcome: fixtureHomeWelcome(fixturePet.pet_id),
    unread: { messages: 2, circle: 3 },
    missing_capabilities: [],
    data_origin: "fixture",
  };
}

export function fixtureFarmAct(body: FarmActionRequestInput, key: string): FarmActionResult {
  if(!body||typeof body!=="object"||Array.isArray(body)||Object.keys(body).some(n=>!["home_id","plot_id","cycle_id","action","crop_key"].includes(n))||typeof key!=="string"||!key.trim())return farmError("invalid_request","请选择有效的菜园操作。",422);
  if(typeof body.home_id!=="string"||typeof body.plot_id!=="string"||(body.cycle_id!=null&&(typeof body.cycle_id!=="string"||!body.cycle_id.trim()))||(body.crop_key!=null&&(typeof body.crop_key!=="string"||!body.crop_key.trim())))return farmError("invalid_request","菜园操作内容不完整。",422);
  const fingerprint=JSON.stringify({home_id:body.home_id,plot_id:body.plot_id,cycle_id:body.cycle_id??null,action:body.action,crop_key:body.crop_key??null});
  const replay=settled.get(key);
  if(replay){if(replay.fingerprint!==fingerprint)return farmError("idempotency_conflict","同一次操作的内容不同。");return structuredClone(replay.result);}
  if(body.home_id!=="fx-home-001")return farmError("missing_home","这个演示家庭不存在。",404);
  const plot=state.plots.find(p=>p.plot_id===body.plot_id);
  if(!plot)return farmError("missing_plot","没有这块地。",404);
  if((body.cycle_id??null)!==plot.cycle_id)return farmError("stale_cycle","这块地已经是另一批作物，请重新查看。");
  const now=Date.now(),current=projectedFarmPlot(plot,now);
  const gained:string[]=[];
  if(body.action==="plant"){
    if(current.stage!=="empty"&&current.stage!=="harvested")return farmError("occupied","这块地还在种着。");
    const crop=FIXTURE_CROPS.find(c=>c.crop_key===body.crop_key);
    if(!crop)return farmError("unknown_crop","这份种子不在当前演示目录中。",422);
    if(!Number.isFinite(now)||!Number.isFinite(crop.grow_seconds)||crop.grow_seconds<=0||!Number.isSafeInteger(crop.yield_units)||crop.yield_units<=0||!Number.isSafeInteger(crop.steal_total)||crop.steal_total<0||crop.steal_total>crop.yield_units)return farmError("invalid_crop","这份演示作物的规则暂未确认。",422);
    const ripeAt=new Date(now+crop.grow_seconds*1000).toISOString();
    if(crop.requires_seed&&!fixtureCollection().some(i=>i.kind==="seed"&&i.item_key===crop.crop_key))return farmError("missing_seed","当前没有这份旅行带回的种子。");
    // All request/plot/rule checks have passed. Consume only the real existing item.
    if(crop.requires_seed&&consumeFixtureSeed(crop.crop_key)===null)return farmError("missing_seed","当前没有这份旅行带回的种子。");
    Object.assign(plot,{stage:"growing",crop_key:crop.crop_key,crop_label:crop.label,ripe_at:ripeAt,cycle_id:`fx-farm-cycle-${++farmCycleSequence}`,steal_total:crop.steal_total,steal_remaining:crop.steal_total});
  }else if(body.action==="harvest"){
    if(current.stage!=="ripe"||!current.cycle_id)return farmError("not_ripe","还没成熟。");
    const crop=FIXTURE_CROPS.find(c=>c.crop_key===current.crop_key);
    if(!crop||(body.crop_key!=null&&body.crop_key!==crop.crop_key))return farmError("unknown_crop","这批作物暂未确认。",422);
    const taken=(current.steal_total??0)-(current.steal_remaining??0),qty=crop.yield_units-taken;
    if(!Number.isSafeInteger(qty)||qty<0)return farmError("invalid_yield","这批收成暂未确认。",422);
    state.pantry[crop.crop_key]=(state.pantry[crop.crop_key]??0)+qty;
    Object.assign(plot,current,{stage:"harvested"});gained.push(`${crop.label} ×${qty} 进了仓库`);
  }else return farmError("invalid_action","自己家的菜不用偷。",422);
  state.version+=1;
  const result:FarmActionResult={plot:{...plot},wallet:wallet(),gained_items:gained};
  settled.set(key,{fingerprint,result:structuredClone(result)});return structuredClone(result);
}
